import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField
} from '@mui/material';
import { format } from 'date-fns';
import { TextBlock, TextSpan } from '../../../Atoms/Text';
import { AccountSelectionForm } from '../../UserCreation/AccountSelectionForm';
import { Account } from '../../../../types';
import { AdvisoryOpportunity, AdviseEngagement, NewEngagementRequest } from '../../../../types/advise';
import { createEngagement, fetchMyOpportunities, findOrCreateAdviseCustomer } from '../../../../Api/Advise';
import { getProductType } from '../../../../constants/advise';
import { getAccounts } from '../../../../store/User';
import { extractApiErrorMessage } from '../../../../utils/extractApiErrorMessage';
import { EMPTY_OPPORTUNITY_FORM, OpportunityForm, OpportunitySection } from './OpportunitySection';
import { EngagementCreatedConfirmation } from './EngagementCreatedConfirmation';
import { defaultEngagementName, defaultOpportunityName, newOpportunityError } from './newEngagementForm';

const PRODUCT_TYPE_ID = 'ccaas';
const NO_ACCOUNTS: Account[] = [];

interface CreatedEngagement {
  engagement: AdviseEngagement;
  customerName: string;
  opportunityIsNew: boolean;
}

export interface NewEngagementDialogProps {
  open: boolean;
  onCancel: () => void;
  onConfigureLater: (engagement: AdviseEngagement) => void;
}

export const NewEngagementDialog = ({ open, onCancel, onConfigureLater }: NewEngagementDialogProps) => {
  const navigate = useNavigate();
  const productName = getProductType(PRODUCT_TYPE_ID)?.name ?? '';
  // A fresh [] each render would make the picker reset its search results on every parent update.
  const bookAccounts = getAccounts() ?? NO_ACCOUNTS;

  const [account, setAccount] = useState<Account | null>(null);
  const [opportunityForm, setOpportunityForm] = useState<OpportunityForm>(EMPTY_OPPORTUNITY_FORM);
  const [opportunities, setOpportunities] = useState<AdvisoryOpportunity[]>([]);
  const [newOpportunityStage, setNewOpportunityStage] = useState('');
  const [salesforceUserFound, setSalesforceUserFound] = useState(true);
  const [loadingOpportunities, setLoadingOpportunities] = useState(false);
  const [engagementName, setEngagementName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<CreatedEngagement | null>(null);
  const opportunityRequest = useRef(0);

  const updateOpportunityForm = (patch: Partial<OpportunityForm>) =>
    setOpportunityForm((current) => ({ ...current, ...patch }));

  useEffect(() => {
    if (!open) return;
    opportunityRequest.current += 1;
    setAccount(null);
    setOpportunityForm(EMPTY_OPPORTUNITY_FORM);
    setOpportunities([]);
    setNewOpportunityStage('');
    setSalesforceUserFound(true);
    setLoadingOpportunities(false);
    setEngagementName('');
    setSaving(false);
    setError(null);
    setCreated(null);
  }, [open]);

  const handleAccountChange = async (next: Account | undefined) => {
    // Bumped first so a lookup still in flight for the previous customer is ignored, even when the field is cleared.
    const request = ++opportunityRequest.current;
    setAccount(next ?? null);
    updateOpportunityForm({ sfId: '' });
    setOpportunities([]);
    setNewOpportunityStage('');
    setSalesforceUserFound(true);
    setLoadingOpportunities(false);
    setError(null);
    if (!next) return;

    updateOpportunityForm({ name: defaultOpportunityName(next, productName) });
    setEngagementName(defaultEngagementName(next, productName));

    setLoadingOpportunities(true);
    const result = await fetchMyOpportunities(next.id);
    if (request !== opportunityRequest.current) return;
    setLoadingOpportunities(false);
    if (result.error) {
      setError(extractApiErrorMessage(result.error, 'Your Opportunities could not be loaded'));
      return;
    }
    setSalesforceUserFound(result.data.salesforce_user_found);
    // Without a Salesforce user there are no Opportunities of their own to link, only a new one to create.
    if (!result.data.salesforce_user_found) setOpportunityForm((current) => ({ ...current, mode: 'new' }));
    setOpportunities(result.data.opportunities);
    setNewOpportunityStage(result.data.new_opportunity_stage);
  };

  const { mode, sfId, name, closeDate } = opportunityForm;
  const opportunityReady = mode === 'existing' ? !!sfId : !newOpportunityError(name, closeDate);
  const canCreate = !!account && !!engagementName.trim() && !loadingOpportunities && opportunityReady && !saving;

  const handleCreate = async () => {
    if (!account || !canCreate) return;
    setSaving(true);
    setError(null);

    const customer = await findOrCreateAdviseCustomer(account.id, account.name);
    if (customer.error) {
      setSaving(false);
      setError(extractApiErrorMessage(customer.error, "The customer's profile could not be set up"));
      return;
    }

    const engagement = {
      account_id: account.id,
      engagement: {
        engagement_name: engagementName.trim(),
        product_type: PRODUCT_TYPE_ID,
        customer_id: customer.data.id
      }
    };
    const request: NewEngagementRequest =
      mode === 'existing'
        ? { ...engagement, opportunity_sf_id: sfId }
        : {
            ...engagement,
            new_opportunity: { name: name.trim(), close_date: format(closeDate as Date, 'yyyy-MM-dd') }
          };
    const result = await createEngagement(request);
    setSaving(false);
    if (result.error) {
      setError(extractApiErrorMessage(result.error, 'The engagement could not be created'));
      return;
    }
    setCreated({ engagement: result.data, customerName: account.name, opportunityIsNew: mode === 'new' });
  };

  const handleConfigureNow = () => {
    if (created) navigate(`/advise/analysis?id=${created.engagement.id}&configure=1`);
  };

  const handleClose = () => {
    if (saving) return;
    if (created) onConfigureLater(created.engagement);
    else onCancel();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      data-testid="new-engagement-dialog">
      <DialogTitle>{created ? 'Engagement created' : 'New Engagement'}</DialogTitle>
      <DialogContent>
        {created ? (
          <EngagementCreatedConfirmation
            engagementName={created.engagement.engagement_name}
            customerName={created.customerName}
            opportunityName={created.engagement.opportunity_name}
            opportunityIsNew={created.opportunityIsNew}
          />
        ) : (
          <div className="flex flex-col gap-5 pt-2">
            <div className="flex flex-col gap-1">
              <TextSpan
                size="xs"
                color="grey6">
                Product
              </TextSpan>
              <TextBlock
                size="sm14"
                color="grey8">
                {productName}
              </TextBlock>
            </div>

            <AccountSelectionForm
              selectedAccount={account}
              setSelectedAccount={handleAccountChange}
              accounts={bookAccounts}
              label="Customer"
              disabled={saving}
            />

            {account && (
              <OpportunitySection
                form={opportunityForm}
                onChange={updateOpportunityForm}
                loading={loadingOpportunities}
                salesforceUserFound={salesforceUserFound}
                opportunities={opportunities}
                newOpportunityStage={newOpportunityStage}
                disabled={saving}
              />
            )}

            {account && (
              <TextField
                required
                fullWidth
                size="small"
                label="Engagement Name"
                value={engagementName}
                disabled={saving}
                error={!engagementName.trim()}
                onChange={(event) => setEngagementName(event.target.value)}
              />
            )}

            {error && <Alert severity="error">{error}</Alert>}
          </div>
        )}
      </DialogContent>
      <DialogActions>
        {created ? (
          <>
            <Button onClick={handleClose}>Configure later</Button>
            <Button
              variant="contained"
              onClick={handleConfigureNow}>
              Configure now
            </Button>
          </>
        ) : (
          <>
            <Button
              onClick={handleClose}
              disabled={saving}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleCreate}
              disabled={!canCreate}
              startIcon={saving ? <CircularProgress size={16} /> : undefined}>
              Create Engagement
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};
